import { useCallback, useState } from "react";
import { useGradeSelectorDispatch, useGradeSelectorSelector } from "../redux/hooks";
import {
    addMultipleChoiceFilterValue,
    addFilterValue,
    addPriceFilterValue,
    clearFilterValue,
    type GradeSelectorFiltersType,
} from "../redux/slices/GradeSelectorFiltersSlice";
import { GradeSelectorFilterId, isMultipleChoiceFilter } from "../utils/filterConstants";
import {
    filterButtonLabelMap,
    type FilterDataType,
    getFilterResultAmount,
    getResultButtonLabel,
    isResultButtonDisabled,
} from "../utils/filterUtils";
import { useCommonLabel } from "../../../../common-deprecated/utils/commonLabels";
import type { GradeSelectorLabelType } from "../utils/labels";
import { trackApplyFilter, trackApplySorting, trackOpenFilter, trackResetFilters } from "../utils/tracking";
import { formatPrice } from "../../../../common-deprecated/Globalize";
import { useCommonSelector } from "../../../../common-deprecated/redux/commonStore";
import type { PriceRangeType } from "../utils/types/Model";

type UseFilterButtonType = {
    onOpenChange: (open: boolean) => void;
    clearFilters: () => void;
    handleColourInput: (id: string) => void;
    handlePriceInput: (price: PriceRangeType, id: string) => void;
    addFilter: () => void;
    selectedFilters: string[];
    setSelectedFilters: (filters: string[]) => void;
    closeLabel: string;
    isButtonDisabled: boolean;
    isOpen: boolean;
    filterResultAmount: number;
    resultButtonLabel: GradeSelectorLabelType;
    buttonLabel: GradeSelectorLabelType;
    buttonText: string;
};

const useFilterButton = (
    filterId: GradeSelectorFilterId,
    activeFilters: GradeSelectorFiltersType["filters"] | undefined,
    filterData: FilterDataType[],
): UseFilterButtonType => {
    const [selectedFilters, setSelectedFilters] = useState<string[]>([]); // Keeps track of the selected but not yet confirmed filters.
    const [isOpen, setIsOpen] = useState(false);
    const [selectedPriceRange, setSelectedPriceRange] = useState<PriceRangeType | undefined>(undefined);
    const dispatch = useGradeSelectorDispatch();

    const filteredPriceRange = useGradeSelectorSelector(
        (state) => state.gradeSelectorFilters.filters[GradeSelectorFilterId.Price],
    );
    const gradeBodyTypes = useGradeSelectorSelector((state) => state.gradeSelectorModel.model.gradeBodyTypes);

    const cultureName = useCommonSelector((state) => state.commonSettings.culture.name);
    const closeLabel = useCommonLabel("close");

    const isButtonDisabled = isResultButtonDisabled(filterId, selectedFilters, activeFilters);
    const filterResultAmount = getFilterResultAmount(
        filterId,
        filterData,
        selectedFilters,
        gradeBodyTypes,
        selectedPriceRange,
    );
    const resultButtonLabel = getResultButtonLabel(isButtonDisabled, filterResultAmount);
    const buttonLabel = filterButtonLabelMap[filterId];
    let buttonText = filterData.find(({ code }) => activeFilters?.[filterId]?.toString() === code)?.name ?? buttonLabel;

    if (filterId === GradeSelectorFilterId.Price) {
        const activePriceRange = activeFilters?.[filterId];
        if (activePriceRange) {
            buttonText = `${formatPrice(filteredPriceRange?.minPrice || 0, cultureName)} - ${formatPrice(filteredPriceRange?.maxPrice || 0, cultureName)}`;
        }
    }

    // Manipulates the components local filter states. This needs to update whenever the Popover opens or closes.
    const onOpenChange = useCallback(
        (open: boolean): void => {
            // Get active checkbox filter data for colour and popular features.
            const activeFilter = activeFilters?.[filterId];

            if (
                (filterId === GradeSelectorFilterId.Colour || filterId === GradeSelectorFilterId.PopularFeatures) &&
                activeFilter !== selectedFilters.toString()
            ) {
                setSelectedFilters(activeFilter ? (activeFilter as string[]) : []);
            } else if (activeFilters) {
                const radioFilterData = filterData.findIndex((data) => data.code === activeFilters[filterId]);

                if (radioFilterData !== -1) {
                    setSelectedFilters([radioFilterData.toString()]);
                } else {
                    setSelectedFilters([]);
                }
            }

            // Get active radio filter data.
            if (activeFilters && !isMultipleChoiceFilter(filterId)) {
                const radioFilterData = filterData.findIndex((data) => data.code === activeFilters[filterId]);
                if (radioFilterData !== -1) {
                    setSelectedFilters([radioFilterData.toString()]);
                } else {
                    setSelectedFilters([]);
                }
            }
            if (open) trackOpenFilter(filterId);
            setIsOpen(open);
        },
        [filterId, filterData, activeFilters, selectedFilters],
    );

    const clearFilters = (): void => {
        trackResetFilters();
        dispatch(clearFilterValue({ filterId }));
    };

    const handleColourInput = useCallback(
        (id: string): void => {
            if (selectedFilters.includes(id)) {
                setSelectedFilters([...selectedFilters.filter((colour) => colour !== id)]);
            } else {
                setSelectedFilters([...selectedFilters, id]);
            }
        },
        [selectedFilters],
    );

    const handlePriceInput = useCallback((price: PriceRangeType, id: string): void => {
        setSelectedFilters([id]);
        setSelectedPriceRange(price);
    }, []);

    const addFilter = useCallback((): void => {
        switch (filterId) {
            case GradeSelectorFilterId.Price:
                dispatch(addPriceFilterValue({ filterId, value: selectedPriceRange }));
                trackApplyFilter(filterId, selectedPriceRange || "");
                break;

            case GradeSelectorFilterId.Colour:
            case GradeSelectorFilterId.PopularFeatures:
                if (selectedFilters.length) {
                    if (isMultipleChoiceFilter(filterId)) {
                        dispatch(
                            addMultipleChoiceFilterValue({
                                filterId,
                                value: filterData
                                    .filter((data) => data.code && selectedFilters.includes(data.code))
                                    .map((data) => data.code),
                            }),
                        );
                    }
                    trackApplyFilter(filterId, selectedFilters);
                } else if (filterResultAmount === 0) {
                    dispatch(clearFilterValue({ filterId }));
                }
                break;

            case GradeSelectorFilterId.Sort:
                dispatch(addFilterValue({ filterId, value: filterData[Number(selectedFilters)].code }));
                trackApplySorting(filterData[Number(selectedFilters)].code);
                break;

            default:
                dispatch(addFilterValue({ filterId, value: filterData[Number(selectedFilters)].code }));
                trackApplyFilter(filterId, filterData[Number(selectedFilters)].code);
                break;
        }

        setIsOpen(!isOpen);
    }, [filterId, filterData, selectedFilters, selectedPriceRange, filterResultAmount, dispatch, isOpen]);

    return {
        addFilter,
        clearFilters,
        handleColourInput,
        handlePriceInput,
        onOpenChange,
        selectedFilters,
        setSelectedFilters,
        resultButtonLabel,
        buttonLabel,
        buttonText,
        closeLabel,
        filterResultAmount,
        isButtonDisabled,
        isOpen,
    };
};

export default useFilterButton;
